import React, { useState, useEffect } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Trash2, Building, Upload, Download, FileText, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IdDocUpload = ({ employeeId, kind, label, info, onChange, disabled }) => {
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('token');
  const hasDoc = !!(info && info.storage_path);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !employeeId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/employee-documents/${employeeId}/${kind}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      onChange(kind, data);
      toast({ title: `${label} uploaded` });
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err.message || err), variant: 'destructive' });
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const removeDoc = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/employee-documents/${employeeId}/${kind}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      onChange(kind, null);
      toast({ title: `${label} removed` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-1" data-testid={`iddoc-${kind}`}>
      {hasDoc ? (
        <>
          <a href={`${API_URL}/api/employee-documents/${employeeId}/${kind}?download=true`}
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline" data-testid={`iddoc-download-${kind}`}>
            <FileText className="h-3.5 w-3.5" /> {info.filename || 'View file'} <Download className="h-3 w-3" />
          </a>
          {!disabled && (
            <button type="button" onClick={removeDoc} disabled={busy} className="text-red-500 hover:text-red-700" data-testid={`iddoc-delete-${kind}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </>
      ) : (
        <label className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${disabled || !employeeId ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 text-gray-600'}`} data-testid={`iddoc-upload-${kind}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {employeeId ? `Upload ${label}` : 'Save employee first'}
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} disabled={disabled || busy || !employeeId} />
        </label>
      )}
    </div>
  );
};

const VisaInfoSection = ({ formData, handleChange, employeeId }) => {
  const [visaCompanies, setVisaCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const isViewer = userRole === 'viewer';

  useEffect(() => {
    fetchVisaCompanies();
  }, []);

  const fetchVisaCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/visa-companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisaCompanies(data);
      }
    } catch (error) {
      console.error('Error fetching visa companies:', error);
      // Fallback to hardcoded values if API fails
      setVisaCompanies([
        { id: '1', name: 'MSBR', is_default: true },
        { id: '2', name: 'SE', is_default: true },
        { id: '3', name: 'SG', is_default: true },
        { id: '4', name: 'Heroes', is_default: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      alert('Please enter a company name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/visa-companies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCompanyName.trim() })
      });

      if (response.ok) {
        await fetchVisaCompanies();
        setNewCompanyName('');
        setDialogOpen(false);
        // Auto-select the newly added company
        handleChange('visa_company', newCompanyName.trim());
      } else {
        const error = await response.json();
        alert(error.detail || 'Error adding company');
      }
    } catch (error) {
      console.error('Error adding company:', error);
      alert('Error adding company');
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete "${companyName}"?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/visa-companies/${companyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchVisaCompanies();
        // Clear selection if deleted company was selected
        if (formData.visa_company === companyName) {
          handleChange('visa_company', '');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Error deleting company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Building className="h-5 w-5 text-blue-600" />
        Visa Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Visa Company</Label>
            {!isViewer && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600 hover:text-blue-700">
                    <Plus className="h-3 w-3 mr-1" />
                    Add Company
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Manage Visa Companies</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Add new company */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="New company name..."
                        value={newCompanyName}
                        onChange={(e) => setNewCompanyName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCompany()}
                        data-testid="new-visa-company-input"
                      />
                      <Button onClick={handleAddCompany} data-testid="add-visa-company-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* List of companies */}
                    <div className="max-h-[250px] overflow-y-auto space-y-2">
                      {visaCompanies.map((company) => (
                        <div key={company.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{company.name}</span>
                            {company.is_default && (
                              <Badge variant="secondary" className="text-xs">Default</Badge>
                            )}
                          </div>
                          {!company.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCompany(company.id, company.name)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <Select 
            value={formData.visa_company || ''} 
            onValueChange={(value) => handleChange('visa_company', value)}
            disabled={loading}
          >
            <SelectTrigger data-testid="employee-visa-company-select">
              <SelectValue placeholder={loading ? "Loading..." : "Select company"} />
            </SelectTrigger>
            <SelectContent>
              {visaCompanies.map((company) => (
                <SelectItem key={company.id} value={company.name}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>ILOE Certificate No.</Label>
          <Input
            value={formData.iloe_certificate_no || ''}
            onChange={(e) => handleChange('iloe_certificate_no', e.target.value)}
            placeholder="e.g. ILOE-2026-0001"
            disabled={isViewer}
            data-testid="employee-iloe-cert-input"
          />
        </div>
        <div>
          <Label>ILOE Expiry Date</Label>
          <Input
            type="date"
            value={formData.iloe_expiry_date ? String(formData.iloe_expiry_date).split('T')[0] : ''}
            onChange={(e) => handleChange('iloe_expiry_date', e.target.value)}
            disabled={isViewer}
            data-testid="employee-iloe-expiry-input"
          />
        </div>
      </div>

      <div className="mt-5 border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Document Files (Visa & Emirates ID)</h4>
        <p className="text-xs text-gray-400 mb-3">Upload a PDF or image. Retrievable here and in the employee portal for audits / renewals.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border p-3 bg-gray-50/50">
            <Label className="text-xs text-gray-600">Visa Document</Label>
            <IdDocUpload
              employeeId={employeeId} kind="visa" label="Visa"
              info={(formData.id_documents || {}).visa}
              disabled={isViewer}
              onChange={(k, data) => handleChange('id_documents', { ...(formData.id_documents || {}), [k]: data || undefined })}
            />
          </div>
          <div className="rounded-lg border p-3 bg-gray-50/50">
            <Label className="text-xs text-gray-600">Emirates ID Document</Label>
            <IdDocUpload
              employeeId={employeeId} kind="emirates_id" label="Emirates ID"
              info={(formData.id_documents || {}).emirates_id}
              disabled={isViewer}
              onChange={(k, data) => handleChange('id_documents', { ...(formData.id_documents || {}), [k]: data || undefined })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaInfoSection;
